<template>
  <v-navigation-drawer
    id="appDrawer"
    :mini-variant.sync="mini"
    fixed
    :dark="$vuetify.dark"
    app
    v-model="drawer"
    width="260"
  >
    <v-toolbar color="primary darken-1" dark>
      <v-toolbar-title class="ml-0 pl-3">
        <span class="hidden-sm-and-down">{{ $t("common.appName") }}</span>
      </v-toolbar-title>
    </v-toolbar>
    <vue-perfect-scrollbar
      class="drawer-menu--scroll"
      style="padding-bottom: 30px"
      :settings="scrollSettings"
    >
      <v-list dense expand>
        <template v-for="(item, i) in menus">
          <!--group with subitems-->
          <v-list-group
            v-if="item.items"
            :key="item.name"
            :group="item.group"
            :prepend-icon="item.icon"
            no-action="no-action"
          >
            <v-list-tile slot="activator" ripple="ripple">
              <v-list-tile-content>
                <v-list-tile-title>{{ $t(item.title) }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <template v-for="(subItem, i) in item.items">
              <!--sub group-->
              <v-list-group
                v-if="subItem.items"
                :key="subItem.name"
                :group="subItem.group"
                sub-group="sub-group"
              >
                <v-list-tile slot="activator" ripple="ripple">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t(subItem.title) }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile
                  v-for="(grand, i) in subItem.children"
                  :key="i"
                  :to="genChildTarget(item, grand)"
                  :href="grand.href"
                  @click="drawer = false"
                  @ripple="ripple"
                >
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t(grand.title) }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list-group>
              <!--child item-->
              <v-list-tile
                v-else
                :key="i"
                :to="genChildTarget(item, subItem)"
                :href="subItem.href"
                @click="drawer = false"
                :disabled="subItem.disabled"
                :target="subItem.target"
                ripple="ripple"
              >
                <v-list-tile-action v-if="subItem.icon" class="sub-item-icon">
                  <v-icon small>{{ subItem.icon }}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title
                    ><span>{{ $t(subItem.title) }}</span></v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action v-if="subItem.action">
                  <v-icon :class="[subItem.actionClass || 'success--text']"
                    >{{ subItem.action }}
                  </v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </template>
          </v-list-group>
          <v-subheader v-else-if="item.header" :key="i">{{
            $t(item.header)
          }}</v-subheader>
          <v-divider v-else-if="item.divider" :key="i"></v-divider>
          <!--top-level link-->
          <v-list-tile
            v-else
            :to="!item.href ? { name: item.name } : null"
            ripple="ripple"
            @click="drawer = false"
            :disabled="item.disabled"
            rel="noopener"
            :key="item.name"
          >
            <v-list-tile-action v-if="item.icon">
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ $t(item.title) }}</v-list-tile-title>
            </v-list-tile-content>
            <v-list-tile-action v-if="item.subAction">
              <v-icon class="success--text">{{ item.subAction }}</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </template>
      </v-list>
    </vue-perfect-scrollbar>
  </v-navigation-drawer>
</template>
<script>
import menu from "@/constants/menu";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

export default {
  name: "app-drawer",
  components: {
    VuePerfectScrollbar,
  },
  props: {
    expanded: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    mini: false,
    drawer: false,
    scrollSettings: {
      maxScrollbarLength: 160,
    },
  }),
  computed: {
    menus() {
      // Group items under their preceding header so a whole section can be
      // hidden when none of its items are visible to the current user.
      const sections = [];
      let current = { header: null, items: [] };
      menu.forEach((item) => {
        if (item.header) {
          current = { header: item, items: [] };
          sections.push(current);
        } else {
          current.items.push(item);
        }
      });

      const result = [];
      sections.forEach(({ header, items }) => {
        const visibleItems = items
          .map((item) => {
            if (item.items) {
              const subItems = item.items.filter(
                ({ permission }) => !permission || this.hasPermission(permission)
              );
              return subItems.length ? { ...item, items: subItems } : null;
            }
            return !item.permission || this.hasPermission(item.permission)
              ? item
              : null;
          })
          .filter(Boolean);

        if (visibleItems.length === 0) return;
        if (header) result.push(header);
        result.push(...visibleItems);
      });

      return result;
    },
    computeGroupActive() {
      return true;
    },
    computeLogo() {
      return "/static/m.png";
    },

    sideToolbarColor() {
      return this.$vuetify.options.extra.sideNav;
    },
  },
  created() {
    window.getApp.$on("APP_DRAWER_TOGGLED", () => {
      this.drawer = !this.drawer;
    });
  },
  methods: {
    genChildTarget(item, subItem) {
      if (subItem.href) return;
      if (subItem.component) {
        return {
          name: subItem.component,
        };
      }
      if (subItem.name) {
        return {
          name: subItem.name,
        };
      }
      return { name: `${item.group}/${subItem.name}` };
    },
  },
};
</script>

<style lang="stylus">
// @import '../../node_modules/vuetify/src/stylus/settings/_elevations.styl';

#appDrawer
    overflow: hidden

    .drawer-menu--scroll
        height: calc(100vh - 48px)
        overflow: auto

    .v-subheader
        height: 36px
        margin-top: 8px
        font-size: 11px
        font-weight: 600
        letter-spacing: .08em
        text-transform: uppercase
        opacity: .55

    .v-list__group__header
        .v-list__tile
            font-weight: 500

    .v-list__tile
        border-radius: 0 20px 20px 0
        margin-right: 8px
        transition: background-color .15s ease

        &:hover
            background-color: rgba(0, 0, 0, .04)

    .v-list__tile--active
        background-color: rgba(215, 16, 16, .08)
        font-weight: 600

        .v-icon
            color: #d71010 !important

    .sub-item-icon
        min-width: 32px

        .v-icon
            opacity: .8

    .v-list__group__items
        .v-list__tile
            padding-left: 8px
</style>
